import { commentService } from '../services/CommentSystem'
import { postController } from './post.controller'
import { isCommentOwnerOrPrivileged } from '../services/permission.service'
import { userService } from '../services/UserSystem'
import { socketService } from '../services/socket.service'

export const commentController = {
  fetchComments,
  fetchCommentById,
  addComment,
  editComment,
  removeComment,
  toggleLike,
  fetchCommentsByUserId,
}

// Fetch comments for a specific post
async function fetchComments(postId) {
  try {
    return await commentService.query({ postId })
  } catch (err) {
    console.error('Error in fetchComments:', err)
    throw err
  }
}

async function fetchCommentsByUserId(userId) {
  try {
    if (!userId) throw new Error('User ID is required')

    // שולח בקשה עם userId
    return await commentService.query({ userId })
  } catch (err) {
    console.error('Error in fetchCommentsByUserId (Controller):', err)
    throw err
  }
}

// Fetch a specific comment by ID
async function fetchCommentById(commentId) {
  try {
    const comment = await commentService.getById(commentId)
    if (!comment) throw new Error(`Comment with ID ${commentId} not found`)
    return comment
  } catch (err) {
    console.error('Error in fetchCommentById:', err)
    throw err
  }
}

async function addComment(commentData, loggedInUser) {
  try {
    if (!loggedInUser) {
      throw new Error('User must be logged in to add a comment.')
    }

    // ✅ ודא שה- `userId` מגיע אך ורק מהמשתמש המחובר
    commentData.userId = loggedInUser._id

    const newComment = await commentService.save(commentData)

    // אם זו תגובת המשך, עדכן את ה-replyCount של התגובה הראשית
    if (newComment.parentCommentId) {
      const parentComment = await commentService.getById(
        newComment.parentCommentId
      )
      if (parentComment) {
        parentComment.replyCount += 1
        await commentService.save(parentComment)
      }
    }

    return newComment
  } catch (err) {
    console.error('Error in addComment:', err)
    throw err
  }
}

// Edit an existing comment
async function editComment(commentId, updatedData, loggedInUser) {
  try {
    const existingComment = await commentService.getById(commentId)
    if (!existingComment) {
      throw new Error(`Comment with ID ${commentId} not found`)
    }

    if (!isCommentOwnerOrPrivileged(loggedInUser, existingComment)) {
      throw new Error('Permission denied: You cannot edit this comment.')
    }

    const updatedComment = {
      ...existingComment,
      ...updatedData,
    }

    return await commentService.save(updatedComment)
  } catch (err) {
    console.error('Error in editComment:', err)
    throw err
  }
}

// Remove a comment by ID
async function removeComment(commentId, loggedInUser) {
  try {
    const commentToRemove = await commentService.getById(commentId)
    if (!commentToRemove) {
      throw new Error(`Comment with ID ${commentId} not found`)
    }

    if (!isCommentOwnerOrPrivileged(loggedInUser, commentToRemove)) {
      throw new Error('Permission denied: You cannot remove this comment.')
    }

    // If it's a reply, update the parent comment's replyCount
    if (commentToRemove.parentCommentId) {
      const parentComment = await commentService.getById(
        commentToRemove.parentCommentId
      )
      if (parentComment) {
        parentComment.replyCount = Math.max(0, parentComment.replyCount - 1)
        await commentService.save(parentComment)
      }
    }

    // Remove the comment itself
    return await commentService.remove(commentId)
  } catch (err) {
    console.error('Error in removeComment:', err)
    throw err
  }
}

// comment.controller.js (Frontend)
export async function toggleLike(commentId, loggedInUser) {
  try {
    if (!loggedInUser || !loggedInUser._id) {
      throw new Error('Unauthorized: Only logged-in users can toggle likes.')
    }

    const updatedComment = await commentService.toggleLike(
      commentId,
      loggedInUser._id
    )

    // Emit event – אם תרצה לשלוח גם מהצד של ה־frontend (במידה ואתה מבצע גם emit ידני)
    socketService.emit('comment-updated', updatedComment)

    return updatedComment
  } catch (err) {
    console.error('❌ Error in toggleLike (controller):', err.message)
    throw err
  }
}
